'use client'

import type React from 'react'
import { useEffect, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'

import Eye from '../../../lib/ui-kit/assets/icons/reeve/banner/eye.svg'
import Unlock from '../../../lib/ui-kit/assets/icons/reeve/banner/unlock.svg'

type ItemType = {
  id: string
  type: 'iconLeft' | 'alone' | 'iconRight'
  chipLabel: string
  chipBg: string
  iconBg?: string
  Icon?: React.ComponentType<{ className?: string }>
  roundness: 'rounded-xl' | 'rounded-full'
}

export const SectionBanner = () => {
  const initialItems: ItemType[] = [
    {
      id: 'unlock',
      type: 'iconLeft',
      chipLabel: 'Unlock',
      chipBg: 'bg-reeve-purple',
      iconBg: 'bg-reeve-blue',
      Icon: Unlock,
      roundness: 'rounded-xl',
    },
    {
      id: 'financial',
      type: 'alone',
      chipLabel: 'Financial',
      chipBg: 'bg-reeve-orange',
      roundness: 'rounded-xl',
    },
    {
      id: 'clarity',
      type: 'iconRight',
      chipLabel: 'Clarity',
      chipBg: 'bg-reeve-yellow',
      iconBg: 'bg-reeve-green',
      Icon: Eye,
      roundness: 'rounded-xl',
    },
  ]

  const [items, setItems] = useState(initialItems)
  const [roundnessPattern, setRoundnessPattern] = useState(0)

  useEffect(() => {
    const shuffleArray = <T,>(arr: T[]): T[] => {
      const a = [...arr]
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[a[i], a[j]] = [a[j], a[i]]
      }
      return a
    }

    // Predefined patterns to avoid chaotic changes
    const roundnessPatterns = [
      [0, 1], // First two chips round
      [1, 2], // Last two chips round
      [0, 2], // First and last chips round
      [0], // Only first chip round
      [1], // Only middle chip round
      [2], // Only last chip round
    ]

    const applyRoundnessPattern = (
      arr: ItemType[],
      patternIndex: number
    ): ItemType[] => {
      const pattern = roundnessPatterns[patternIndex]
      return arr.map((item, index) => ({
        ...item,
        roundness: pattern.includes(index) ? 'rounded-full' : 'rounded-xl',
      }))
    }

    const interval = setInterval(() => {
      setItems((prev) => {
        // First shuffle positions
        const shuffled = shuffleArray(prev)

        // Then apply new roundness pattern after a slight delay
        setTimeout(() => {
          setItems((current) => {
            const nextPattern =
              (roundnessPattern + 1) % roundnessPatterns.length
            setRoundnessPattern(nextPattern)
            return applyRoundnessPattern(current, nextPattern)
          })
        }, 150) // Small delay to let shuffle animation start first

        return shuffled
      })
    }, 2000)

    return () => clearInterval(interval)
  }, [roundnessPattern])

  return (
    <section className="m-auto flex flex-col items-center justify-center gap-x-2 gap-y-2 bg-[#EFEDE5] px-6 py-12 xl:flex-row xl:px-10 xl:py-24 2xl:py-40">
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <motion.div
            key={item.id}
            layout
            transition={{
              type: 'spring',
              stiffness: 120,
              damping: 25,
              duration: 0.8,
            }}
            className={
              item.type === 'alone'
                ? ''
                : item.type === 'iconLeft'
                  ? 'flex flex-row items-center gap-x-2'
                  : 'flex flex-row-reverse items-center gap-x-2 xl:flex-row'
            }
          >
            {item.type === 'iconLeft' && (
              <motion.div
                layout
                animate={{
                  borderRadius:
                    item.roundness === 'rounded-xl' ? '12px' : '50px',
                }}
                transition={{
                  layout: {
                    type: 'spring',
                    stiffness: 120,
                    damping: 25,
                    duration: 0.8,
                  },
                  borderRadius: {
                    type: 'spring',
                    stiffness: 80,
                    damping: 20,
                    duration: 0.7,
                    delay: 0.1,
                  },
                }}
                className={`${item.chipBg} px-6 py-4 text-[3rem] font-normal leading-[3.5rem] text-reeve-primary 2xl:text-[4rem] 2xl:leading-[4.5rem]`}
              >
                {item.chipLabel}
              </motion.div>
            )}
            {item.type === 'alone' && (
              <motion.div
                layout
                animate={{
                  borderRadius:
                    item.roundness === 'rounded-xl' ? '12px' : '50px',
                }}
                transition={{
                  layout: {
                    type: 'spring',
                    stiffness: 120,
                    damping: 25,
                    duration: 0.8,
                  },
                  borderRadius: {
                    type: 'spring',
                    stiffness: 80,
                    damping: 20,
                    duration: 0.7,
                    delay: 0.1,
                  },
                }}
                className={`${item.chipBg} px-6 py-4 text-[3rem] font-normal leading-[3.5rem] text-reeve-primary 2xl:text-[4rem] 2xl:leading-[4.5rem]`}
              >
                {item.chipLabel}
              </motion.div>
            )}
            {item.type === 'iconRight' && (
              <motion.div
                layout
                animate={{
                  borderRadius:
                    item.roundness === 'rounded-xl' ? '12px' : '50px',
                }}
                transition={{
                  layout: {
                    type: 'spring',
                    stiffness: 120,
                    damping: 25,
                    duration: 0.8,
                  },
                  borderRadius: {
                    type: 'spring',
                    stiffness: 80,
                    damping: 20,
                    duration: 0.7,
                    delay: 0.1,
                  },
                }}
                className={`${item.chipBg} px-6 py-4 text-[3rem] font-normal leading-[3.5rem] text-reeve-primary 2xl:text-[4rem] 2xl:leading-[4.5rem]`}
              >
                {item.chipLabel}
              </motion.div>
            )}
            {(item.type === 'iconLeft' || item.type === 'iconRight') &&
              item.Icon && (
                <div className={`${item.iconBg} rounded-full p-3 2xl:p-5`}>
                  <item.Icon className="h-16 w-16 fill-reeve-primary" />
                </div>
              )}
          </motion.div>
        ))}
      </AnimatePresence>
    </section>
  )
}
